<script>
// Shared category (ExhibitorCategory) multi-select facet, used by the exhibitor,
// community, session and speaker list navbars. Fetches the category tree and
// renders the first two levels (0-1) as an expandable tree: level-0 rows can be
// collapsed/expanded (chevron) and are themselves selectable, level-1 children
// appear as checkboxes when expanded.
// v-model = array of selected category ids.

export default {
  name: 'CategoryFacet',
  props: {
    value: { type: Array, required: false, default: () => [] }, // selected ids
    eventShortName: { type: String, required: false, default: '' },
    label: { type: String, required: false, default: 'Kategorie' },
    // '' renders a plain text-label dropdown (like the talk navbar's Bühne);
    // an icon renders the icon tool-button variant.
    icon: { type: String, required: false, default: 'fas fa-tags' },
  },
  data() {
    return {
      tree: [],          // [{id, name, children: [{id, name}]}] limited to level 0-1
      showDropdown: false,
      expanded: {},      // level-0 id -> bool
    }
  },
  computed: {
    hasActive() {
      return this.value && this.value.length > 0
    },
  },
  created() {
    this.fetchCategories()
  },
  methods: {
    fetchCategories() {
      const params = {}
      if (this.eventShortName) params.event__short_name = this.eventShortName.toUpperCase()
      this.$api.get('/exhibitor-categories/', { params }).then(({ data }) => {
        this.tree = (data || []).map(n => ({
          id: n.id,
          name: n.name,
          children: (n.children || []).map(c => ({ id: c.id, name: c.name })),
        }))
      }).catch(() => {})
    },
    isSelected(id) {
      // loose equality: query param ids may be strings
      for (const v of this.value) if (v == id) return true
      return false
    },
    toggle(id) {
      const sel = this.value.slice()
      const i = sel.findIndex(v => v == id)
      if (i >= 0) sel.splice(i, 1)
      else sel.push(id)
      this.$emit('input', sel)
      this.$emit('change', sel)
    },
    isExpanded(id) {
      return !!this.expanded[id]
    },
    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },
  },
}
</script>

<template>
  <li class="nav-item dropdown">
    <div v-if="icon" class="btn btn-sm navbar-btn me-1 mb-1 c-tool-btn"
     :class="hasActive ? 'text-primary' : ''" style="cursor:pointer"
     :title="label" @click.prevent="showDropdown=!showDropdown">
      <i :class="icon"></i>
      <span class="c-tool-btn-label">{{ label }}</span>
    </div>
    <div v-else class="btn bg-secondary w-100 btn-sm-lg navbar-btn dropdown-toggle me-1 mb-1"
     @click.prevent="showDropdown=!showDropdown">
      {{ label }}
    </div>

    <!-- .show renders the Bootstrap dropdown (v-if toggles existence).
         Positioning lives in .c-cat-menu: absolute below the toggle on
         desktop, fixed drawer-style overlay on mobile. -->
    <div v-if="showDropdown" class="dropdown-menu show p-2 c-cat-menu">
      <div v-for="node in tree" :key="node.id" class="c-cat-node">
        <div class="d-flex align-items-start">
          <span class="text-muted text-center flex-shrink-0 me-2" style="width:1rem; cursor:pointer"
                @click.prevent="toggleExpand(node.id)">
            <i v-if="node.children.length" style="font-size:.7rem"
               :class="isExpanded(node.id) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
          </span>
          <label class="mb-0" style="cursor:pointer">
            <input type="checkbox" :checked="isSelected(node.id)" @change="toggle(node.id)" />&nbsp; {{ node.name }}
          </label>
        </div>
        <div v-if="isExpanded(node.id)" class="ms-4">
          <label v-for="child in node.children" :key="child.id" class="dropdown-item mb-0 ps-1" style="cursor:pointer">
            <input type="checkbox" :checked="isSelected(child.id)" @change="toggle(child.id)" />&nbsp; {{ child.name }}
          </label>
        </div>
      </div>
    </div>
  </li>
</template>

<style scoped>
/* Desktop: absolute below the toggle, sized to the longest category name
   so labels don't wrap. width: max-content grows with the content; the
   max-width cap keeps it inside the viewport. */
.c-cat-menu {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1056;            /* above navbar, below modals */
    max-height: 60vh;
    overflow-y: auto;
    min-width: 16rem;
    width: max-content;
    /* 44rem fits the longest category name (~94 chars) without wrapping */
    max-width: min(92vw, 44rem);
}
.c-cat-menu label {
    white-space: nowrap;
}
/* Zebra striping per top-level group (children inherit the parent's stripe). */
.c-cat-node {
    padding: .2rem .35rem;
    border-radius: .25rem;
}
.c-cat-node:nth-child(odd) {
    background-color: rgba(0, 0, 0, .04);
}
@media (max-width: 575.98px) {
    /* Mobile: drawer-style overlay fixed to the viewport (the absolute
       left:0 of the toggle would push the menu past the right edge).
       95% of the viewport width; anything longer than that may wrap. */
    .c-cat-menu {
        position: fixed;
        left: 2.5vw;
        right: 2.5vw;
        top: 4.5rem;
        width: auto;           /* left+right = 95vw */
        min-width: 0;
        max-width: none;
        max-height: 75vh;
    }
    .c-cat-menu label {
        white-space: normal;
    }
}

/* Icon tool-button variant (icon + small caption), matching Favoriten/History. */
.c-tool-btn {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.2;
}
.c-tool-btn-label {
    font-size: 0.65rem;
    margin-top: 2px;
}

/* Text-label variant sizing, matching the sibling filters (Bühne/Sprache). */
@media screen and (min-width: 960px) {
    .btn-sm-lg {
        padding: 0.25rem 0.5rem;
        font-size: 0.8rem;
        line-height: 1.5;
        border-radius: 0rem;
    }
}
</style>
